import { useQueryState } from "nuqs";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "../localization";
import { useDebouncedEffect } from "./use-debounced-effect";

export function useS(disableUrlQuery?: boolean, keyPrefix?: string) {
	const [searchValue, setSearchValue] = useQueryState(
		keyPrefix ? `${keyPrefix}-searchValue` : "searchValue",
		{
			defaultValue: "",
			clearOnDefault: true,
		},
	);
	const [inlineSearchValue, setInlineSearchValue] = useState("");

	return {
		searchValue: !disableUrlQuery ? searchValue : inlineSearchValue,
		setSearchValue: !disableUrlQuery ? setSearchValue : setInlineSearchValue,
	};
}

export function useSearchForm(keyPrefix = "", timeout = 150, disableUrlQuery = false) {
	/** State */
	const { searchValue, setSearchValue } = useS(disableUrlQuery, keyPrefix);

	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm<{ searchValue: string }>({
		defaultValues: {
			searchValue,
		},
	});
	const _searchValue = useWatch({
		control: methods.control,
		name: "searchValue",
	});

	/** Effects */
	useDebouncedEffect(
		() => {
			setSearchValue(_searchValue);
		},
		[_searchValue],
		timeout,
	);

	function getSearchInputProps() {
		return {
			placeholder: t("placeholders.search"),
			type: "search",
			control: methods.control,
			name: "searchValue",
			label: undefined,
			isRequired: false,
		} as const;
	}

	function getSearchInputWrapperProps(withMaxWidth = true) {
		return {
			style: {
				flex: 1,
				maxWidth: withMaxWidth ? 400 : undefined,
			},
		} as const;
	}

	return { ...methods, searchValue, getSearchInputProps, getSearchInputWrapperProps };
}

export type SearchForm = ReturnType<typeof useSearchForm>;
